import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { IconButton, Tooltip, Typography } from "@mui/material";
import AutorenewOutlinedIcon from "@mui/icons-material/AutorenewOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { FileText } from "lucide-react";

import {
  FilePreviewModal,
  UIProgressBar,
  UIUpload,
} from "../../../../ui";
import { clx } from "../../../../../utils/Helpers";
import { EVIDENCE_ACCEPT } from "./publishPayConstants";
import { UPLOAD_STATUS } from "./useEvidenceUpload";

const INPUT_ID = "publish-pay-transfer-evidence";

/** Latin digits in both languages; the unit comes from the locale ("MB" / "ميجابايت"). */
const formatFileSize = (bytes, t) =>
  bytes >= 1024 * 1024
    ? t.fileSizeMb.replace(
        "{size}",
        (bytes / (1024 * 1024)).toLocaleString("en-US", { maximumFractionDigits: 1 }),
      )
    : t.fileSizeKb.replace(
        "{size}",
        Math.max(1, Math.round(bytes / 1024)).toLocaleString("en-US"),
      );

const TransferEvidence = ({ t, shared, previewT, isRtl, upload, errorText }) => {
  const [previewUrl, setPreviewUrl] = useState("");
  /** The full name is offered in a tooltip only while it is cut off. */
  const [nameTruncated, setNameTruncated] = useState(false);
  const { status, file, progress, uploadId } = upload;
  const isUploaded = status === UPLOAD_STATUS.uploaded;

  useEffect(
    () => () => {
      if (previewUrl.startsWith("blob:")) URL.revokeObjectURL(previewUrl);
    },
    [previewUrl],
  );

  const pickFile = () => document.getElementById(INPUT_ID)?.click();

  const actions = isUploaded
    ? [
        {
          label: t.previewFile,
          Icon: VisibilityOutlinedIcon,
          // A proof saved with the draft has no local copy: it opens from its link.
          onClick: () =>
            setPreviewUrl(file instanceof Blob ? URL.createObjectURL(file) : file.url),
        },
        { label: t.replaceFile, Icon: AutorenewOutlinedIcon, onClick: pickFile },
        { label: t.removeFile, Icon: DeleteOutlineIcon, onClick: upload.clear },
      ]
    : [{ label: t.cancelUpload, Icon: CloseIcon, onClick: upload.clear }];

  return (
    <div className="publish-pay-step__evidence">
      <UIUpload
        id={INPUT_ID}
        label={t.uploadProof}
        accept={EVIDENCE_ACCEPT}
        uploadText={t.uploadText}
        dragHint={t.dragHint}
        hint={t.uploadHint}
        error={Boolean(errorText)}
        helperText={errorText}
        onChange={(selectedFile) => selectedFile && upload.start(selectedFile)}
        errorAction={
          <button
            type="button"
            className="publish-pay-step__retry"
            onClick={pickFile}
          >
            {t.tryAnotherFile}
          </button>
        }
      />

      {file && (
        <div
          className={clx("publish-pay-step__file", {
            "publish-pay-step__file--uploaded": isUploaded,
          })}
        >
          <div className="publish-pay-step__file-row">
            <FileText
              size={20}
              className="publish-pay-step__file-icon"
              aria-hidden
            />
            <span className="publish-pay-step__file-name">
              <Tooltip title={nameTruncated ? file.name : ""}>
                <Typography
                  component="span"
                  className="text-label-md"
                  // An English name stays left-to-right in Arabic, so it is cut at its end.
                  dir="auto"
                  onMouseEnter={({ currentTarget }) =>
                    setNameTruncated(
                      currentTarget.scrollWidth > currentTarget.clientWidth,
                    )
                  }
                >
                  {file.name}
                </Typography>
              </Tooltip>
              {isUploaded && file.size != null && (
                <Typography component="span" className="text-body-xs publish-pay-step__file-size">
                  {formatFileSize(file.size, t)}
                </Typography>
              )}
            </span>
            <Typography
              component="span"
              className="text-heading-lg publish-pay-step__file-meta"
              dir="ltr"
            >
              {progress}
              <span>%</span>
            </Typography>
            {actions.map(({ label, Icon, onClick }) => (
              <Tooltip key={label} title={label}>
                <IconButton
                  type="button"
                  size="small"
                  aria-label={label}
                  onClick={onClick}
                >
                  <Icon fontSize="small" />
                </IconButton>
              </Tooltip>
            ))}
          </div>
          <UIProgressBar
            key={uploadId}
            value={progress}
            barColor={
              isUploaded
                ? "var(--color-success-default)"
                : "var(--color-primary-default)"
            }
            size="sm"
            aria-label={file.name}
          />
        </div>
      )}

      <FilePreviewModal
        open={Boolean(previewUrl)}
        fileName={file?.name || ""}
        contentType={file?.type}
        fileUrl={previewUrl}
        isRtl={isRtl}
        labels={{
          close: shared.close,
          unsupported: previewT.unsupportedType,
          loadError: previewT.loadError,
          pageOf: previewT.pageOf,
          previousPage: previewT.previousPage,
          nextPage: previewT.nextPage,
        }}
        onClose={() => setPreviewUrl("")}
      />
    </div>
  );
};

TransferEvidence.propTypes = {
  t: PropTypes.object.isRequired,
  shared: PropTypes.object.isRequired,
  /** File preview modal copy (shared with the client reports preview). */
  previewT: PropTypes.object.isRequired,
  isRtl: PropTypes.bool,
  upload: PropTypes.shape({
    status: PropTypes.string.isRequired,
    file: PropTypes.object,
    progress: PropTypes.number.isRequired,
    uploadId: PropTypes.number,
    start: PropTypes.func.isRequired,
    clear: PropTypes.func.isRequired,
  }).isRequired,
  /** Shown under the upload when "Submit for Approval" was pressed with no file. */
  errorText: PropTypes.string,
};

TransferEvidence.defaultProps = {
  isRtl: false,
  errorText: undefined,
};

export default TransferEvidence;
